<template>
  <b-card body-class="governing-bodies__card">
    <h6 class="dashboard-pages__title">Uploaded Documents</h6>
    <p class="document-explanation">
      View and preview your uploaded identity documents linked to your profile.
    </p>

    <!-- Filter and Sort Controls -->
    <div v-if="hasDocuments" class="mb-3">
      <b-row>
        <b-col md="6">
          <b-form-group
            label="Filter by Type"
            label-class="control-label"
            class="mb-2"
          >
            <SearchableDropdown
              v-model="selectedFilter"
              :options="searchableFilterOptions"
              placeholder="Select document type..."
              :group-by="'category'"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Sort by Expiry Date"
            label-class="control-label"
            class="mb-2"
          >
            <div class="sort-toggle-container">
              <b-button
                :variant="sortAscending ? 'primary' : 'outline-primary'"
                size="sm"
                @click="sortAscending = true"
                class="sort-toggle-btn"
                title="Sort by expiry date - newest first"
              >
                Newest First
              </b-button>
              <b-button
                :variant="!sortAscending ? 'primary' : 'outline-primary'"
                size="sm"
                @click="sortAscending = false"
                class="sort-toggle-btn"
                title="Sort by expiry date - oldest first"
              >
                Oldest First
              </b-button>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <div v-if="hasDocuments">
      <b-row>
        <b-col md="6">
          <div class="document-list">
            <div
              v-for="doc in documents"
              :key="doc.id"
              @click="selectDocument(doc)"
              :class="[
                'document-item',
                {
                  selected: selectedDocument && selectedDocument.id === doc.id,
                },
              ]"
              @mouseenter="handleMouseEnter(doc)"
              @mouseleave="handleMouseLeave"
            >
              <div class="document-preview-thumb">
                <div v-if="isPDF(doc)" class="pdf-thumb-container">
                  <pdf style="width: 100%" :resize="true" :src="doc.url"></pdf>
                </div>
                <b-img v-else :src="doc.url" class="document-thumb" />
              </div>
              <div class="document-info">
                <p class="document-name mb-0">{{ doc.name }}</p>
                <small class="document-type">{{ doc.type }}</small>
                <div v-if="doc.uploadDate || doc.expiryDate" class="date-info">
                  <small class="date-line">
                    <span v-if="doc.uploadDate">
                      <strong>Uploaded:</strong>
                      {{ formatDate(doc.uploadDate) }}
                    </span>
                    <span v-if="doc.expiryDate">
                      <strong>Expires:</strong> {{ formatDate(doc.expiryDate) }}
                    </span>
                  </small>
                </div>
              </div>
              <div class="document-actions">
                <div
                  class="download-icon"
                  @click.stop="downloadDocument(doc)"
                  title="Download document"
                >
                  <DownloadIcon />
                </div>
                <div
                  class="delete-icon"
                  @click.stop="confirmDeleteDocument(doc)"
                  title="Delete document"
                >
                  <DeleteIcon />
                </div>
              </div>
              <div
                v-if="doc.expiryDate"
                class="expiry-indicator"
                :class="getExpiryStatusClass(doc.expiryDate)"
              ></div>
            </div>
          </div>
        </b-col>

        <b-col md="6">
          <b-card body-class="preview-card__body">
            <h5 class="card-title__bold">Preview</h5>
            <small class="file-name" v-if="selectedDocument">{{
              selectedDocument.name
            }}</small>
            <div v-if="selectedDocument" class="expand-hint">
              <small class="text-muted"> Click image below to expand </small>
            </div>

            <div
              v-for="doc in documents"
              :key="doc.id"
              v-show="selectedDocument && selectedDocument.id === doc.id"
              class="previewing-image__container preview-being__hovered"
            >
              <div
                v-if="isPDF(doc)"
                class="pdf-preview-wrapper"
                @click="openImageModal(doc)"
              >
                <pdf
                  style="width: 100%"
                  :resize="false"
                  :src="doc.url"
                  class="previewing-image"
                ></pdf>
              </div>
              <b-img
                v-else
                :src="doc.url"
                class="previewing-image"
                @click="openImageModal(doc)"
              />
            </div>

            <div v-show="!selectedDocument" class="no-preview">
              <p class="text-muted text-center">Select a document to preview</p>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <div v-else class="no-documents">
      <p class="text-muted text-center">No documents have been uploaded yet.</p>
    </div>

    <!-- Fullscreen Image Modal -->
    <b-modal
      v-model="showImageModal"
      hide-header
      hide-footer
      centered
      size="xl"
      dialog-class="modal-image-preview"
    >
      <div class="modal-image-container">
        <div
          v-if="selectedDocument && isPDF(selectedDocument)"
          class="modal-pdf-container"
        >
          <pdf
            style="width: 100%"
            :resize="true"
            :src="selectedDocument.url"
            class="modal-pdf"
          ></pdf>
        </div>
        <b-img
          v-else-if="selectedDocument"
          :src="selectedDocument.url"
          class="modal-image"
        />
      </div>
      <div class="modal-close-hint">
        <small class="text-muted">Click outside to close</small>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import DownloadIcon from "vue-material-design-icons/Download";
import DeleteIcon from "vue-material-design-icons/Delete";
import SearchableDropdown from "../common/SearchableDropdown.vue";
import {
  DOCUMENT_TYPES,
  getSearchableDocumentTypes,
  getDocumentTypeText,
} from "@/constants/documentTypes";

export default {
  name: "DocumentPreview",
  components: {
    pdf: () => import("pdfvuer"),
    DownloadIcon,
    DeleteIcon,
    SearchableDropdown,
  },
  props: {
    personalProfile: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      allDocuments: [], // Store all documents
      selectedDocument: null,
      hoverTimeout: null,
      preloadedDocuments: new Set(), // Cache for documents that have been preloaded
      selectedFilter: "all", // Default to 'all' types
      sortAscending: true, // Sort by expiry date - true for soonest first, false for latest first
      showImageModal: false,
    };
  },
  computed: {
    searchableFilterOptions() {
      return getSearchableDocumentTypes();
    },
    documents() {
      // Start with all documents
      let docs = [...(this.allDocuments || [])];

      // Filter by type if needed
      if (this.selectedFilter && this.selectedFilter !== "all") {
        docs = docs.filter((d) => d.rawType === this.selectedFilter);
      }

      // Sort by expiry date
      const toDate = (v) => (v ? new Date(v) : null);

      if (this.sortAscending) {
        // Soonest first (ascending)
        docs.sort((a, b) => {
          const da = toDate(a.expiryDate);
          const db = toDate(b.expiryDate);
          if (!da && !db) return 0;
          if (!da) return 1; // nulls last
          if (!db) return -1;
          return da - db;
        });
      } else {
        // Latest first (descending)
        docs.sort((a, b) => {
          const da = toDate(a.expiryDate);
          const db = toDate(b.expiryDate);
          if (!da && !db) return 0;
          if (!da) return 1; // nulls last
          if (!db) return -1;
          return db - da;
        });
      }

      return docs;
    },
    hasDocuments() {
      return this.allDocuments && this.allDocuments.length > 0;
    },
  },
  watch: {
    personalProfile: {
      handler(newProfile, oldProfile) {
        if (newProfile && !oldProfile) {
          this.loadDocuments();
        }
      },
      immediate: true,
    },
  },
  methods: {
    loadDocuments() {
      this.allDocuments = [];

      if (!this.personalProfile) {
        return;
      }

      this.$api.v2
        .get("identity-documents")
        .then((response) => {
          const identityDocuments = response.body || [];

          this.allDocuments = identityDocuments.map((doc) => {
            return {
              id: doc.id,
              uploadStorageId: doc.uploadStorageId,
              name: doc.name,
              type: this.formatDocumentType(doc.type),
              rawType: doc.type,
              fileType: doc.fileType,
              url: `${process.env.VUE_APP_API_ROOT}file/${doc.uploadStorageId}`,
              expiryDate: doc.expiryDate,
              uploadDate: doc.uploadDate,
            };
          });

          if (this.allDocuments.length > 0) {
            this.selectedDocument = this.allDocuments[0];
          }
        })
        .catch((error) => {
          console.error("Failed to load identity documents:", error);
          this.allDocuments = [];
        });
    },
    selectDocument(doc) {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
      this.selectedDocument = doc;
      this.preloadDocument(doc);
    },
    handleMouseEnter(doc) {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }

      this.hoverTimeout = setTimeout(() => {
        this.selectedDocument = doc;
        this.preloadDocument(doc);
        this.hoverTimeout = null;
      }, 500);
    },
    handleMouseLeave() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
    },
    preloadDocument(doc) {
      if (!this.preloadedDocuments.has(doc.id)) {
        this.preloadedDocuments.add(doc.id);
        if (!this.isPDF(doc)) {
          const img = new Image();
          img.src = doc.url;
        }
      }
    },
    openImageModal(doc) {
      // Ensure selectedDocument aligns with the clicked one
      this.selectedDocument = doc;
      this.showImageModal = true;
    },
    formatDocumentType(rawType) {
      // Use the helper function from constants to get the display text
      return getDocumentTypeText(rawType);
    },

    formatDate(dateString) {
      if (!dateString) return "";

      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    getExpiryStatusClass(expiryDate) {
      if (!expiryDate) return "";

      const today = new Date();
      const expiry = new Date(expiryDate);
      const timeDiff = expiry.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      if (daysDiff <= 0) {
        return "expiry-expired";
      } else if (daysDiff <= 30) {
        return "expiry-warning expiry-flashing";
      } else if (daysDiff <= 90) {
        return "expiry-warning";
      } else {
        return "expiry-valid";
      }
    },

    isPDF(doc) {
      return doc.fileType && doc.fileType.toLowerCase() === "pdf";
    },

    downloadDocument(doc) {
      window.open(doc.url, "_blank");
    },

    confirmDeleteDocument(doc) {
      const message = `Are you sure you want to delete "${doc.name}"?\n\nThis action cannot be undone.`;

      if (confirm(message)) {
        this.deleteDocument(doc);
      }
    },

    deleteDocument(doc) {
      // First delete the document record from the database
      this.$api.v2
        .delete(`identity-documents/${doc.id}`)
        .then(() => {
          // After successfully deleting the document record, delete the associated file
          return this.$api.v2.delete(`file/${doc.uploadStorageId}`);
        })
        .then(() => {
          this.$success(
            "Document Deleted",
            `${doc.name} has been successfully deleted.`
          );

          // Remove the document from the local array
          this.allDocuments = this.allDocuments.filter((d) => d.id !== doc.id);

          // If the deleted document was selected, select a new one or clear selection
          if (this.selectedDocument && this.selectedDocument.id === doc.id) {
            this.selectedDocument =
              this.documents.length > 0 ? this.documents[0] : null;
          }

          // Emit event to parent component to refresh if needed
          this.$emit("document-deleted", doc);
        })
        .catch((error) => {
          console.error("Failed to delete document:", {
            documentId: doc.id,
            uploadStorageId: doc.uploadStorageId,
            error: error,
            errorMessage: error.message,
            errorStatus: error.status,
            errorBody: error.body,
            timestamp: new Date().toISOString(),
          });

          // Check if it's a file deletion error after successful document deletion
          if (
            error.status === 404 &&
            error.url &&
            error.url.includes("/file/")
          ) {
            // File might already be deleted or not found, but document was deleted successfully
            this.$warning(
              "Document Deleted",
              `${doc.name} was deleted, but the associated file could not be found or was already removed.`
            );

            // Still update the UI since the document record was deleted
            this.allDocuments = this.allDocuments.filter(
              (d) => d.id !== doc.id
            );
            if (this.selectedDocument && this.selectedDocument.id === doc.id) {
              this.selectedDocument =
                this.documents.length > 0 ? this.documents[0] : null;
            }
            this.$emit("document-deleted", doc);
          } else {
            this.$httpError("Failed to delete document", error);
          }
        });
    },
  },
  beforeDestroy() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  },
};
</script>

<style scoped>
.document-list {
  display: flex;
  flex-direction: column;
  max-height: 450px;
  overflow-y: auto;
  border-right: 1px solid #e9ecef;
  padding-right: 10px;
}

@media (max-width: 767px) {
  .document-list {
    margin-bottom: 20px;
  }
}

.document-item {
  cursor: pointer;
  display: flex;
  align-items: center;
  border: 1px solid #e9ecef;
  padding: 8px;
  margin-bottom: 8px;
  border-radius: 8px;
  background-color: white;
  transition: all 0.3s ease;
  position: relative;
}

.document-item:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.document-item.selected {
  border-color: #007bff;
  background-color: #e7f3ff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.document-preview-thumb {
  width: 50px;
  height: 50px;
  overflow: hidden;
  border-radius: 8px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #ccc;
}

.pdf-thumb-container {
  border-radius: 8px;
  max-height: 50px;
  max-width: 50px;
  width: 100%;
  height: 100%;
}

.pdf-thumb-container .page {
  height: 100%;
  width: 100%;
}

.pdf-thumb-container .canvasWrapper {
  height: 100%;
  width: 100%;
}

.pdf-thumb-container #viewerContainer {
  height: 100%;
}

.document-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.document-name {
  font-weight: 600;
}

.file-name {
  display: block;
  margin-bottom: 10px;
}

.no-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.card-title__bold {
  font-size: 14px;
  font-weight: 600;
}

.previewing-image__container {
  height: 400px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.previewing-image {
  border-radius: 8px;
  border: 1px solid #ccc;
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.previewing-image:hover {
  opacity: 0.9;
}

.pdf-preview-wrapper {
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.pdf-preview-wrapper:hover {
  opacity: 0.9;
}

.document-explanation {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.dashboard-pages__title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.preview-card__body {
  background-color: #f8f9fa;
}

.preview-being__hovered {
  border-radius: 8px;
  padding: 4px;
}

.governing-bodies__card {
  background-color: #f8f9fa;
}

.date-info {
  margin-top: 2px;
}

.date-line {
  font-size: 11px;
  font-weight: 500;
}

.expiry-indicator {
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  width: 8px;
  border-top-right-radius: 7px;
  border-bottom-right-radius: 7px;
}

/* Expiry status color classes */
.expiry-expired {
  background-color: #dc3545;
}

.expiry-warning {
  background-color: #ffc107;
}

.expiry-valid {
  background-color: #28a745;
}

/* Flashing animation for 1-month expiry warning */
.expiry-flashing {
  animation: flash-warning 1.5s infinite;
}

@keyframes flash-warning {
  0% {
    background-color: #ffc107;
    opacity: 1;
  }
  50% {
    background-color: #ffc107;
    opacity: 0.3;
  }
  100% {
    background-color: #ffc107;
    opacity: 1;
  }
}

.document-actions {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.download-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.download-icon:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.download-icon:active {
  transform: scale(0.95);
}

.delete-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #dc3545;
}

.delete-icon:hover {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.delete-icon:active {
  transform: scale(0.95);
}

.no-documents {
  padding: 2rem;
  text-align: center;
}

/* Sort Toggle Controls */
.control-label {
  font-weight: 600;
  font-size: 12px;
  color: #495057;
  margin-bottom: 0.25rem;
}

.sort-toggle-container {
  display: flex;
}

.sort-toggle-btn {
  border-radius: 0 !important;
  border: none !important;
  font-size: 14px;
  padding: 6px 12px;
  white-space: nowrap;
}

.sort-toggle-btn:first-child {
  border-top-left-radius: 3px !important;
  border-bottom-left-radius: 3px !important;
}

.sort-toggle-btn:last-child {
  border-top-right-radius: 3px !important;
  border-bottom-right-radius: 3px !important;
}

.sort-toggle-btn + .sort-toggle-btn {
  border-left: 1px solid rgba(255, 255, 255, 0.2) !important;
}

/* Image preview modal styles */
.modal-image-preview .modal-dialog {
  max-width: 90vw;
}

.modal-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-pdf-container {
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
}

.modal-pdf {
  width: 100%;
  display: block;
}

.modal-close-hint {
  text-align: center;
  margin-top: 15px;
}

.expand-hint {
  text-align: center;
  margin-bottom: 8px;
}

/* Use consistent site primary color for inactive toggle buttons */
.sort-toggle-btn.btn-outline-primary {
  border: 1px solid #2c3e50 !important;
  color: #2c3e50 !important;
}

.sort-toggle-btn.btn-outline-primary:hover {
  background-color: rgba(44, 62, 80, 0.1) !important;
  border: 1px solid #2c3e50 !important;
  color: #2c3e50 !important;
}
</style>
